import { MAX_FONT_FAMILY_NAME_LENGTH } from './fontFamilyName.js'

export const FONT_RECORD_VERSION = 2 as const
export const MAX_STORED_FONT_BYTES = 20 * 1024 * 1024
export const MAX_ENCODED_FONT_BASE64_LENGTH = Math.ceil(MAX_STORED_FONT_BYTES / 3) * 4

const FONT_FORMATS = ['ttf', 'otf', 'woff', 'woff2'] as const

export type FontFormat = (typeof FONT_FORMATS)[number]
export type FontBinaryData = Blob | ArrayBuffer

export interface FontSummary {
  readonly name: string
  readonly format: FontFormat
  readonly dateAdded: number
}

export interface BinaryFontRecord extends FontSummary {
  readonly version: typeof FONT_RECORD_VERSION
  readonly data: FontBinaryData
}

export interface FontRecordStore {
  get(name: string): Promise<unknown>
  set(name: string, value: BinaryFontRecord): Promise<void>
  delete(name: string): Promise<void>
  /** Returns only small metadata records; implementations must not hydrate font bytes. */
  listMetadata(): Promise<readonly unknown[]>
}

export interface FontFaceResource {
  load(): Promise<unknown>
}

export interface FontRuntime<TFace extends FontFaceResource = FontFaceResource> {
  createObjectURL(data: Blob): string
  revokeObjectURL(url: string): void
  createFontFace(family: string, source: string): TFace
  addFontFace(face: TFace): void
  deleteFontFace(face: TFace): void
}

export interface FontRegistryOptions {
  readonly activeFont?: string
}

export interface FontShellOptions<TFonts> {
  readonly prepareDefaultFont: () => Promise<void>
  readonly loadSavedFonts: () => Promise<TFonts>
  readonly onSavedFonts: (fonts: TFonts) => void
  readonly onSavedFontsError: (error: unknown) => void
}

export function isActiveFontReady(
  shellReady: boolean,
  activeFont: string,
  registeredActiveFont: string,
  defaultFont: string,
): boolean {
  return shellReady
    && (activeFont === defaultFont || registeredActiveFont === activeFont)
}

export interface UnavailableFontRecovery {
  readonly activeFont: string
  readonly recoveredFrom: string
}

/** Explicit recovery used only after the currently selected font is unavailable. */
export function resolveUnavailableActiveFont(
  activeFont: string,
  defaultFont: string,
): UnavailableFontRecovery | null {
  return activeFont === defaultFont
    ? null
    : { activeFont: defaultFont, recoveredFrom: activeFont }
}

export function resolveFailedActiveFontReplacement(
  activeFont: string,
  replacedFont: string,
  saveCommitted: boolean,
  defaultFont: string,
): UnavailableFontRecovery | null {
  return saveCommitted && activeFont === replacedFont
    ? resolveUnavailableActiveFont(activeFont, defaultFont)
    : null
}

type UnknownRecord = Record<string, unknown>

interface RegistrationResource<TFace extends FontFaceResource> {
  readonly url: string
  readonly face: TFace
  added: boolean
  faceReleased: boolean
  urlReleased: boolean
  released: boolean
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isFontFormat(value: unknown): value is FontFormat {
  return typeof value === 'string' && FONT_FORMATS.includes(value as FontFormat)
}

function summaryFrom(value: unknown): FontSummary | null {
  try {
    if (!isRecord(value)) return null
    const name = value.name
    const format = value.format
    const dateAdded = value.dateAdded
    if (typeof name !== 'string'
      || name.length === 0
      || name.length > MAX_FONT_FAMILY_NAME_LENGTH
      || !isFontFormat(format)
      || typeof dateAdded !== 'number'
      || !Number.isFinite(dateAdded)) {
      return null
    }

    return { name, format, dateAdded }
  } catch {
    return null
  }
}

export function readIndexedFontSummary(indexedName: string, value: unknown): FontSummary | null {
  const candidate = summaryFrom(value)
  return candidate !== null && candidate.name === indexedName ? candidate : null
}

function isBlob(value: unknown): value is Blob {
  return typeof Blob !== 'undefined' && value instanceof Blob
}

function copyBinary(value: unknown): FontBinaryData | null {
  if (value instanceof ArrayBuffer) {
    try {
      const byteLength = value.byteLength
      if (byteLength === 0 || byteLength > MAX_STORED_FONT_BYTES) return null
      const copy: unknown = value.slice(0)
      return copy instanceof ArrayBuffer && copy.byteLength === byteLength
        ? copy
        : null
    } catch {
      return null
    }
  }

  if (isBlob(value)) {
    try {
      const size = value.size
      const type = value.type
      if (size === 0 || size > MAX_STORED_FONT_BYTES) return null
      const copy: unknown = value.slice(0, size, type)
      return isBlob(copy) && copy.size === size ? copy : null
    } catch {
      return null
    }
  }

  return null
}

export function decodeLegacyFontBase64(value: unknown): ArrayBuffer | null {
  if (typeof value !== 'string'
    || value.length === 0
    || value.length > MAX_ENCODED_FONT_BASE64_LENGTH
    || !/^[A-Za-z0-9+/]+={0,2}$/u.test(value)
    || value.length % 4 !== 0) {
    return null
  }

  try {
    const decoded = globalThis.atob(value)
    if (decoded.length === 0 || decoded.length > MAX_STORED_FONT_BYTES) return null
    const bytes = new Uint8Array(decoded.length)
    for (let index = 0; index < decoded.length; index += 1) {
      bytes[index] = decoded.charCodeAt(index)
    }
    return bytes.buffer
  } catch {
    return null
  }
}

function normalizeRecord(value: unknown): { record: BinaryFontRecord; legacy: boolean } | null {
  const summary = summaryFrom(value)
  if (summary === null || !isRecord(value)) return null

  try {
    const version = value.version
    if (version === FONT_RECORD_VERSION) {
      const data = copyBinary(value.data)
      return data === null
        ? null
        : { record: { version: FONT_RECORD_VERSION, ...summary, data }, legacy: false }
    }

    if (version !== undefined) return null
    const data = decodeLegacyFontBase64(value.base64Data)
    return data === null
      ? null
      : { record: { version: FONT_RECORD_VERSION, ...summary, data }, legacy: true }
  } catch {
    return null
  }
}

function snapshotRecord(input: BinaryFontRecord): BinaryFontRecord {
  const summary = summaryFrom(input)
  const data = copyBinary(input.data)
  if (input.version !== FONT_RECORD_VERSION || summary === null || data === null) {
    throw new TypeError('Invalid or unsupported font record')
  }
  return { version: FONT_RECORD_VERSION, ...summary, data }
}

function fontMimeType(format: FontFormat): string {
  switch (format) {
    case 'otf': return 'font/otf'
    case 'woff': return 'font/woff'
    case 'woff2': return 'font/woff2'
    default: return 'font/ttf'
  }
}

/**
 * Resolves after the bundled/default font is usable, while intentionally
 * leaving saved-font discovery in the background. This keeps app-shell
 * readiness independent from the size of IndexedDB font storage.
 */
export async function startFontShell<TFonts>(options: FontShellOptions<TFonts>): Promise<void> {
  await options.prepareDefaultFont()
  void Promise.resolve()
    .then(options.loadSavedFonts)
    .then(options.onSavedFonts)
    .catch((error: unknown) => {
      options.onSavedFontsError(error)
    })
}

/**
 * Owns lazy binary hydration and browser font resources. Storage and browser
 * primitives are injected so lifecycle behavior is deterministic in tests.
 */
export class FontRegistry<TFace extends FontFaceResource = FontFaceResource> {
  private readonly loads = new Map<string, Promise<BinaryFontRecord | null>>()
  private readonly operations = new Map<string, Promise<void>>()
  private readonly mutations = new Map<string, Promise<void>>()
  private readonly pending = new Map<string, Promise<boolean>>()
  private readonly deferredRegistrations = new Map<string, Promise<boolean>>()
  private readonly deferredRegistrationTokens = new Map<string, symbol>()
  private readonly resources = new Map<string, RegistrationResource<TFace>>()
  private readonly cleanupBacklog = new Set<RegistrationResource<TFace>>()
  private readonly generations = new Map<string, number>()
  private disposed = false
  private selectedFont: string

  constructor(
    private readonly storage: FontRecordStore,
    private readonly runtime: FontRuntime<TFace>,
    options: FontRegistryOptions = {},
  ) {
    this.selectedFont = options.activeFont ?? ''
  }

  get activeFont(): string {
    return this.selectedFont
  }

  setActiveFont(name: string): void {
    if (typeof name !== 'string' || name.length === 0 || name.length > MAX_FONT_FAMILY_NAME_LENGTH) {
      throw new RangeError(`Font name must contain 1-${MAX_FONT_FAMILY_NAME_LENGTH} characters`)
    }
    this.selectedFont = name
  }

  /** Object URL owned by a fully loaded registration, for worker font loading. */
  getRegisteredSource(name: string): string | null {
    const resource = this.resources.get(name)
    return resource !== undefined && resource.added && !resource.released
      ? resource.url
      : null
  }

  async list(): Promise<FontSummary[]> {
    const values = await this.storage.listMetadata()
    const unique = new Map<string, FontSummary>()
    for (const value of values) {
      const summary = summaryFrom(value)
      if (summary !== null) unique.set(summary.name, summary)
    }
    return [...unique.values()].sort((left, right) => (
      right.dateAdded - left.dateAdded || left.name.localeCompare(right.name)
    ))
  }

  load(name: string): Promise<BinaryFontRecord | null> {
    const existing = this.loads.get(name)
    if (existing !== undefined) return existing

    const loading = this.enqueue(name, () => this.loadRecord(name))
    this.loads.set(name, loading)
    void loading.then(
      () => { if (this.loads.get(name) === loading) this.loads.delete(name) },
      () => { if (this.loads.get(name) === loading) this.loads.delete(name) },
    )
    return loading
  }

  save(input: BinaryFontRecord): Promise<void> {
    if (this.disposed) return Promise.reject(new Error('Font registry is disposed'))
    const record = snapshotRecord(input)
    return this.trackMutation(record.name, this.enqueue(record.name, async () => {
      await this.storage.set(record.name, record)
      this.unregister(record.name, true)
      this.loads.delete(record.name)
    }))
  }

  async delete(name: string): Promise<void> {
    await this.trackMutation(name, this.enqueue(name, async () => {
      await this.storage.delete(name)
      this.unregister(name, true)
      this.loads.delete(name)
    }))
  }

  register(name: string): Promise<boolean> {
    if (this.disposed) return Promise.reject(new Error('Font registry is disposed'))
    const deferredExisting = this.deferredRegistrations.get(name)
    if (deferredExisting !== undefined) return deferredExisting
    const mutation = this.mutations.get(name)
    if (mutation !== undefined) {
      const token = Symbol(name)
      const deferred = Promise.resolve().then(() => this.registerWhenMutationsSettle(
        name,
        () => this.deferredRegistrationTokens.get(name) === token,
      ))
      this.deferredRegistrations.set(name, deferred)
      this.deferredRegistrationTokens.set(name, token)
      void deferred.then(
        () => {
          if (this.deferredRegistrationTokens.get(name) === token) {
            this.deferredRegistrations.delete(name)
            this.deferredRegistrationTokens.delete(name)
          }
        },
        () => {
          if (this.deferredRegistrationTokens.get(name) === token) {
            this.deferredRegistrations.delete(name)
            this.deferredRegistrationTokens.delete(name)
          }
        },
      )
      return deferred
    }
    return this.getOrBeginRegistration(name)
  }

  private getOrBeginRegistration(name: string): Promise<boolean> {
    const existing = this.pending.get(name)
    if (existing !== undefined) return existing
    const registration = this.beginRegistration(name)
    this.pending.set(name, registration)
    void registration.then(
      () => { if (this.pending.get(name) === registration) this.pending.delete(name) },
      () => { if (this.pending.get(name) === registration) this.pending.delete(name) },
    )
    return registration
  }

  private async registerWhenMutationsSettle(
    name: string,
    isCurrent: () => boolean,
  ): Promise<boolean> {
    while (isCurrent()) {
      const mutation = this.mutations.get(name)
      if (mutation === undefined) return this.getOrBeginRegistration(name)
      try {
        await mutation
      } catch {
        // Failed storage mutations preserve the prior resource/load ownership.
      }
    }
    return false
  }

  unregister(name: string, preserveDeferred = false): readonly unknown[] {
    this.generations.set(name, this.generation(name) + 1)
    this.pending.delete(name)
    if (!preserveDeferred) {
      this.deferredRegistrations.delete(name)
      this.deferredRegistrationTokens.delete(name)
    }
    const resource = this.resources.get(name)
    if (resource !== undefined) {
      this.resources.delete(name)
      return this.release(resource)
    }
    return []
  }

  releaseAll(): readonly unknown[] {
    const errors: unknown[] = []
    const previousFailures = [...this.cleanupBacklog]
    const names = new Set([
      ...this.resources.keys(),
      ...this.pending.keys(),
      ...this.deferredRegistrations.keys(),
    ])
    for (const name of names) errors.push(...this.unregister(name))
    for (const resource of previousFailures) errors.push(...this.release(resource))
    return errors
  }

  dispose(): readonly unknown[] {
    if (this.disposed) {
      const errors: unknown[] = []
      for (const resource of [...this.cleanupBacklog]) errors.push(...this.release(resource))
      return errors
    }
    this.disposed = true
    const errors = this.releaseAll()
    this.loads.clear()
    return errors
  }

  private async loadRecord(name: string): Promise<BinaryFontRecord | null> {
    const value = await this.storage.get(name)
    const normalized = normalizeRecord(value)
    if (normalized === null) return null
    if (normalized.record.name !== name) return null
    if (normalized.legacy) {
      // IndexedDB set is atomic: a rejected migration keeps the legacy value.
      await this.storage.set(name, normalized.record)
    }
    return normalized.record
  }

  private beginRegistration(name: string): Promise<boolean> {
    if (this.disposed) return Promise.reject(new Error('Font registry is disposed'))
    if (this.resources.get(name)?.added === true) return Promise.resolve(true)
    return this.registerLoaded(name, this.generation(name))
  }

  private async registerLoaded(name: string, generation: number): Promise<boolean> {
    const record = await this.load(name)
    if (record === null || !this.isCurrent(name, generation)) return false

    const blob = isBlob(record.data)
      ? record.data
      : new Blob([record.data], { type: fontMimeType(record.format) })
    const url = this.runtime.createObjectURL(blob)
    let resource: RegistrationResource<TFace> | undefined
    try {
      const face = this.runtime.createFontFace(record.name, `url("${url}")`)
      resource = {
        url,
        face,
        added: false,
        faceReleased: false,
        urlReleased: false,
        released: false,
      }
      this.resources.set(name, resource)
      await face.load()
      if (!this.isCurrent(name, generation) || this.resources.get(name) !== resource) {
        this.release(resource)
        return false
      }
      resource.added = true
      this.runtime.addFontFace(face)
      return true
    } catch (error) {
      if (resource !== undefined && this.resources.get(name) === resource) {
        this.resources.delete(name)
      }
      if (resource === undefined) {
        this.runtime.revokeObjectURL(url)
      } else {
        this.release(resource)
      }
      throw error
    }
  }

  private release(resource: RegistrationResource<TFace>): readonly unknown[] {
    if (resource.released) return []
    const errors: unknown[] = []
    if (resource.added && !resource.faceReleased) {
      try {
        this.runtime.deleteFontFace(resource.face)
        resource.faceReleased = true
      } catch (error) {
        errors.push(error)
      }
    }
    if (!resource.urlReleased) {
      try {
        this.runtime.revokeObjectURL(resource.url)
        resource.urlReleased = true
      } catch (error) {
        errors.push(error)
      }
    }
    resource.released = (!resource.added || resource.faceReleased) && resource.urlReleased
    if (resource.released) {
      this.cleanupBacklog.delete(resource)
    } else {
      this.cleanupBacklog.add(resource)
    }
    return errors
  }

  private generation(name: string): number {
    return this.generations.get(name) ?? 0
  }

  private isCurrent(name: string, generation: number): boolean {
    return !this.disposed && this.generation(name) === generation
  }

  private enqueue<TResult>(name: string, operation: () => Promise<TResult>): Promise<TResult> {
    const previous = this.operations.get(name) ?? Promise.resolve()
    const result = previous.catch(() => undefined).then(operation)
    const tail = result.then(
      () => undefined,
      () => undefined,
    )
    this.operations.set(name, tail)
    void tail.then(() => {
      if (this.operations.get(name) === tail) this.operations.delete(name)
    })
    return result
  }

  private trackMutation<TResult>(name: string, result: Promise<TResult>): Promise<TResult> {
    const tail = result.then(
      () => undefined,
      () => undefined,
    )
    this.mutations.set(name, tail)
    void tail.then(() => {
      if (this.mutations.get(name) === tail) this.mutations.delete(name)
    })
    return result
  }
}
